import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Form, Button, Row, Col, Spinner, Alert, Table
} from 'react-bootstrap';
import { IconPlus, IconMinus, IconArrowLeft } from '@tabler/icons-react';
import Select, { components } from '../../components/form/AppSelect';
import NumberInput from '../../components/form/NumberInput';
import { useTranslation } from 'react-i18next';
import { api } from '../../shared/api';
import { formatDate } from '../../shared/utils/dateUtils';
import '../../shared/ReactSelectDarkMode.css';
import { useQuery, useMutation, useInfiniteQuery } from '@tanstack/react-query';
import PageShell from '../../components/layout/PageShell';
import PageSection from '../../components/layout/PageSection';
import { CenteredLoader } from '../../components/LoadingSpinner';

// Debounce function to limit API calls during typing
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Custom MenuList component for infinite scroll
const MenuList = (props) => {
  const { onLoadMore, hasMore, isLoading } = props.selectProps;
  const scrollElementRef = useRef(null);

  const handleScroll = useCallback((e) => {
    const element = e.target;
    const { scrollTop, scrollHeight, clientHeight } = element;

    // Check if scrolled to bottom (within 10px threshold)
    if (scrollHeight - scrollTop <= clientHeight + 10) {
      if (hasMore && !isLoading && onLoadMore) {
        onLoadMore();
      }
    }
  }, [hasMore, isLoading, onLoadMore]);

  // Use effect to add scroll listener to the menu list element
  useEffect(() => {
    const element = scrollElementRef.current;
    if (element) {
      element.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        element.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll]);

  return (
    <components.MenuList
      {...props}
      innerRef={scrollElementRef}
    >
      {props.children}
      {isLoading && (
        <div className="d-flex justify-content-center p-2">
          <Spinner size="sm" animation="border" />
          <span className="ms-2">Loading more items...</span>
        </div>
      )}
      {!hasMore && props.children && props.children.length > 0 && (
        <div className="text-center p-2 text-muted small">
          No more items to load
        </div>
      )}
    </components.MenuList>
  );
};

// Custom theme for React Select to support dark mode
const selectTheme = theme => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary: 'var(--tblr-primary)',
    primary75: 'var(--tblr-primary-rgb)',
    primary50: 'var(--tblr-primary-rgb)',
    primary25: 'var(--tblr-primary-rgb)',
    danger: 'var(--tblr-danger)',
    dangerLight: 'var(--tblr-danger-rgb)',
    neutral0: 'var(--tblr-bg-forms)',
    neutral5: 'var(--tblr-bg-surface)',
    neutral10: 'var(--tblr-bg-surface)',
    neutral20: 'var(--tblr-border-color)',
    neutral30: 'var(--tblr-border-color)',
    neutral40: 'var(--tblr-body-color-secondary)',
    neutral50: 'var(--tblr-body-color-secondary)',
    neutral60: 'var(--tblr-body-color-secondary)',
    neutral70: 'var(--tblr-body-color-secondary)',
    neutral80: 'var(--tblr-body-color)',
    neutral90: 'var(--tblr-body-color)'
  }
});

const RentalAdd = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState([]);
  const [error, setError] = useState(null);
  const [warehouseSearch, setWarehouseSearch] = useState('');
  const [debouncedWarehouseSearch, setDebouncedWarehouseSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedWarehouseSearch(warehouseSearch), 300);
    return () => clearTimeout(timer);
  }, [warehouseSearch]);

  const { isLoading: loading, data: baseData } = useQuery({
    queryKey: ['rental-add-form-data'],
    queryFn: async () => {
      const [customersRes, reasonsRes, crewRes] = await Promise.all([
        api.get('customers/api/customers/'),
        api.get('rentals/api/rental-reasons/'),
        api.get('crew/api/crew/')
      ]);
      const sortedCustomers = customersRes.sort((a, b) => a.company_name.localeCompare(b.company_name));
      return { customers: sortedCustomers, reasons: reasonsRes, crew: crewRes };
    },
  });

  const customerOptions = (baseData?.customers ?? []).map(c => ({ value: c.id, label: c.company_name }));
  const reasonOptions = (baseData?.reasons ?? []).map(r => ({ value: r.id, label: r.name }));
  const crewOptions = (baseData?.crew ?? []).map(m => ({ value: m.id, label: `${m.company.company_name} | ${m.display_name}` }));

  const {
    data: itemsData,
    isLoading: itemsInitialLoading,
    isFetchingNextPage,
    hasNextPage: hasMoreItems,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['warehouse-items-all', debouncedWarehouseSearch],
    queryFn: async ({ pageParam = 1 }) => {
      const params = new URLSearchParams({ page: pageParam, ordering: 'name' });
      if (debouncedWarehouseSearch) params.append('search', debouncedWarehouseSearch);
      return api.getRaw(`warehouses/api/stock-items/?${params}`);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage?.next ? new URL(lastPage.next).searchParams.get('page') : undefined,
  });

  const itemsLoading = itemsInitialLoading || isFetchingNextPage;
  const allItems = itemsData?.pages.flatMap(p => p.results ?? p.items ?? []) ?? [];
  const itemOptions = allItems.map(item => ({
    value: item.id,
    label: `${item.product_name} (${t('rentals.add.available')}: ${item.quantity_available})`,
    data: item,
  }));
  const totalItemCount = itemsData?.pages[0]?.count ?? 0;

  const submitMutation = useMutation({
    mutationFn: (formattedValues) => api.post('rentals/api/rentals/', formattedValues),
    onSuccess: (rentalResponse) => navigate(`/rentals/rental/${rentalResponse.id}`),
    onError: () => setError(t('rentals.add.errors.failedToCreate')),
  });
  const submitting = submitMutation.isPending;

  const [formData, setFormData] = useState({
    customer: null,
    rental_reason: null,
    date_from: '',
    date_to: '',
    issued_by: null,
    rental_date: formatDate(new Date()),
    rental_status: 'active',
    note: ''
  });

  // Load more items for infinite scroll
  const loadMoreItems = useCallback(() => {
    if (hasMoreItems && !itemsLoading) {
      fetchNextPage();
    }
  }, [hasMoreItems, itemsLoading, fetchNextPage]);

  // Handle item search with debounce
  const handleItemSearch = (inputValue, actionMeta) => {
    if (actionMeta.action === 'input-change') {
      setWarehouseSearch(inputValue);
    }
  };

  const handleAddItem = () => {
    const currentItems = [...selectedItems];
    const newItem = {
      key: Date.now(),
      item: null, // Changed to null for react-select
      quantity: 1
    };
    currentItems.push(newItem);
    setSelectedItems(currentItems);
  };

  const handleRemoveItem = (key) => {
    const currentItems = selectedItems.filter(item => item.key !== key);
    setSelectedItems(currentItems);
  };

  const handleItemChange = (key, field, value) => {
    const updatedItems = selectedItems.map(item => {
      if (item.key === key) {
        if (field === 'item') {
          // When item changes, reset quantity to 1 and clear any errors
          setError(null);
          return { ...item, [field]: value, quantity: 1 };
        }
        if (field === 'quantity') {
          // Find the selected warehouse item to check availability
          const selectedWarehouseItem = allItems.find(warehouseItem => warehouseItem.id === item.item);
          if (selectedWarehouseItem && value > selectedWarehouseItem.quantity_available) {
            setError(t('rentals.add.validation.quantityExceeded', {
              name: selectedWarehouseItem.product_name,
              available: selectedWarehouseItem.quantity_available
            }));
            return item; // Don't update if quantity exceeds available
          }
          // Clear error if quantity is valid
          setError(null);
        }
        return { ...item, [field]: value };
      }
      return item;
    });
    setSelectedItems(updatedItems);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.customer || !formData.rental_reason || !formData.issued_by) {
      setError(t('rentals.add.validation.requiredFields'));
      return;
    }
    if (selectedItems.length === 0 || selectedItems.some(item => !item.item)) {
      setError(t('rentals.add.validation.selectItems'));
      return;
    }
    for (const selectedItem of selectedItems) {
      const warehouseItem = allItems.find(item => item.id === selectedItem.item);
      if (warehouseItem && selectedItem.quantity > warehouseItem.quantity_available) {
        setError(t('rentals.add.validation.quantityExceededDetailed', { name: warehouseItem.product_name, available: warehouseItem.quantity_available, requested: selectedItem.quantity }));
        return;
      }
      if (warehouseItem && warehouseItem.quantity_available === 0) {
        setError(t('rentals.add.validation.itemNotAvailable', { name: warehouseItem.product_name }));
        return;
      }
    }
    const formattedValues = {
      customer: formData.customer, rental_reason: formData.rental_reason,
      issued_by: formData.issued_by,
      date_from: new Date(formData.date_from).toISOString(),
      date_to: new Date(formData.date_to).toISOString(),
      rental_date: formData.rental_date, rental_status: formData.rental_status,
      note: formData.note, tariff_type: 'day',
      items: selectedItems.map(item => ({ stock_item: item.item, quantity: item.quantity }))
    };
    submitMutation.mutate(formattedValues);
  };

  if (loading) {
    return (
      <PageShell pretitle={t('rentals.title')} title={t('rentals.add.title')}>
        <PageSection>
          <CenteredLoader />
        </PageSection>
      </PageShell>
    );
  }

  return (
    <PageShell
      pretitle={t('rentals.title')}
      title={t('rentals.add.title')}
      description={t('rentals.add.subtitle', 'Založ novou zápůjčku, vyber zákazníka, termín a přidej skladové položky do jednoho formuláře.')}
      actions={
        <Button
          variant="outline-secondary"
          className="me-0"
          onClick={() => navigate('/rentals')}
        >
          <IconArrowLeft size={16} className="me-2" />
          {t('common.back')}
        </Button>
      }
    >
      <PageSection>
              {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                  {error}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('rentals.add.customerRequired')}</Form.Label>
                      <Select
                        name="customer"
                        options={customerOptions}
                        value={customerOptions.find(option => option.value === formData.customer)}
                        onChange={option => setFormData({ ...formData, customer: option.value })}
                        required
                        theme={selectTheme}
                        isClearable
                        classNamePrefix="react-select"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('rentals.add.rentalReasonRequired')}</Form.Label>
                      <Select
                        name="rental_reason"
                        options={reasonOptions}
                        value={reasonOptions.find(option => option.value === formData.rental_reason)}
                        onChange={option => setFormData({ ...formData, rental_reason: option.value })}
                        required
                        theme={selectTheme}
                        isClearable
                        classNamePrefix="react-select"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('rentals.add.dateFromRequired')}</Form.Label>
                      <Form.Control
                        type="datetime-local"
                        name="date_from"
                        value={formData.date_from}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('rentals.add.dateToRequired')}</Form.Label>
                      <Form.Control
                        type="datetime-local"
                        name="date_to"
                        value={formData.date_to}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('rentals.add.issuedByRequired')}</Form.Label>
                      <Select
                        name="issued_by"
                        options={crewOptions}
                        value={crewOptions.find(option => option.value === formData.issued_by)}
                        onChange={option => setFormData({ ...formData, issued_by: option.value })}
                        required
                        theme={selectTheme}
                        isClearable
                        classNamePrefix="react-select"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('rentals.add.rentalDateRequired')}</Form.Label>
                      <Form.Control
                        type="date"
                        name="rental_date"
                        value={formData.rental_date}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>{t('rentals.add.note')}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="note"
                    value={formData.note}
                    onChange={handleInputChange}
                  />
                </Form.Group>

                <h5 className="mt-4 mb-3">{t('rentals.add.rentalItems')}</h5>

                {selectedItems.length > 0 ? (
                  <Table striped bordered hover responsive className="mb-3">
                    <thead>
                      <tr>
                        <th>{t('rentals.add.selectItem')}</th>
                        <th>{t('rentals.add.quantity')}</th>
                        <th>{t('rentals.add.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedItems.map(item => (
                        <tr key={item.key}>
                          <td>
                            <Select
                              options={itemOptions}
                              value={itemOptions.find(option => option.value === item.item) || null}
                              onChange={(option) => handleItemChange(item.key, 'item', option ? option.value : null)}
                              onInputChange={handleItemSearch}
                              placeholder={t('rentals.add.selectItem')}
                              theme={selectTheme}
                              classNamePrefix="react-select"
                              className="react-select-container"
                              components={{ MenuList }}
                              onLoadMore={loadMoreItems}
                              hasMore={hasMoreItems}
                              isLoading={itemsLoading}
                              totalCount={totalItemCount}
                              isClearable
                              isSearchable
                            />
                          </td>
                          <td>
                            {(() => {
                              const selectedWarehouseItem = allItems.find(warehouseItem => warehouseItem.id === item.item);
                              const maxQuantity = selectedWarehouseItem ? selectedWarehouseItem.quantity_available : 1;

                              return (
                                <NumberInput
                                  min={1}
                                  max={maxQuantity}
                                  step="any"
                                  value={item.quantity}
                                  onChange={(value) => handleItemChange(item.key, 'quantity', value)}
                                  disabled={!item.item}
                                />
                              );
                            })()}
                          </td>
                          <td className="text-center">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleRemoveItem(item.key)}
                            >
                              <IconMinus size={16} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <Alert variant="info">
                    {t('rentals.add.noItemsAdded')}
                  </Alert>
                )}

                <div className="mb-4">
                  <Button
                    variant="outline-primary"
                    onClick={handleAddItem}
                    className="w-100"
                  >
                    <IconPlus size={16} className="me-1" /> {t('rentals.add.addItem')}
                  </Button>
                </div>

                <div className="d-grid">
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={submitting || selectedItems.length === 0}
                  >
                    {submitting ? <Spinner as="span" size="sm" animation="border" className="me-2" /> : null}
                    {submitting ? t('rentals.add.creating') : t('rentals.add.createRental')}
                  </Button>
                </div>
              </Form>
      </PageSection>
    </PageShell>
  );
};

export default RentalAdd;
